/**
 * FlowableのBPMN拡張語彙の唯一の置き場。パーサー(model.ts)とbpmn-js Viewerが同じ名前を参照する。
 * bpmn-js/bpmn-moddleへは依存させず、素の文字列とmoddle descriptor（ただのオブジェクト）だけを置く。
 */
export const FLOWABLE_NS = 'http://flowable.org/bpmn';
export const FLOWABLE_PREFIX = 'flowable';

/** 既存BPMN要素へ付くflowable:属性のローカル名。 */
export const FlowableAttr = {
  /** serviceTaskの実行方式。 */
  type: 'type',
  /** external-worker serviceTaskのtopic。 */
  topic: 'topic',
  assignee: 'assignee',
  collection: 'collection',
  elementVariable: 'elementVariable',
} as const;

/** extensionElements配下へ置くflowable:要素のローカル名。 */
export const FlowableEl = {
  externalWorkerInParameter: 'externalWorkerInParameter',
  externalWorkerOutParameter: 'externalWorkerOutParameter',
  field: 'field',
  variableAggregation: 'variableAggregation',
  variable: 'variable',
} as const;

/** serviceTaskのflowable:typeに入る値。この2種以外は初期runtimeの許可構文外。 */
export const FlowableTaskType = { externalWorker: 'external-worker', dmn: 'dmn' } as const;

/** DMN Taskが判定表を指すflowable:fieldのname。 */
export const DECISION_TABLE_REFERENCE_KEY = 'decisionTableReferenceKey';

const pascal = (localName: string): string => `${localName[0]!.toUpperCase()}${localName.slice(1)}`;

/** moddle要素から属性を読むときの名前。'type' → 'flowable:type'。 */
export const flowableAttrName = (localName: string): string => `${FLOWABLE_PREFIX}:${localName}`;

/** moddle上の型名。要素名の先頭を大文字にしたものになる。'field' → 'flowable:Field'。 */
export const flowableTypeName = (localName: string): string =>
  `${FLOWABLE_PREFIX}:${pascal(localName)}`;

const attr = (name: string, type: 'String' | 'Boolean' = 'String') => ({
  name,
  isAttr: true,
  type,
});

const workerParamProperties = [
  attr('source'),
  attr('sourceExpression'),
  attr('target'),
  attr('targetExpression'),
  attr('transient', 'Boolean'),
];

/**
 * bpmn-moddle / bpmn-jsへ渡すFlowable拡張の定義。ここに無いflowable:属性・要素は
 * moddleが未知として警告し、model.tsが「未対応拡張」として利用者へ知らせる。
 * 拒否対象（class/delegateExpression/listener等）は意図的に定義しない。
 */
export const flowableModdleDescriptor = {
  name: 'Flowable',
  uri: FLOWABLE_NS,
  prefix: FLOWABLE_PREFIX,
  xml: { tagAlias: 'lowerCase' },
  types: [
    {
      name: pascal(FlowableEl.externalWorkerInParameter),
      superClass: ['Element'],
      properties: workerParamProperties,
    },
    {
      name: pascal(FlowableEl.externalWorkerOutParameter),
      superClass: ['Element'],
      properties: workerParamProperties,
    },
    {
      name: pascal(FlowableEl.field),
      superClass: ['Element'],
      // stringValue属性形式と、子要素<flowable:string>/<flowable:expression>形式の両方を読む。
      properties: [
        attr('name'),
        attr('stringValue'),
        { name: 'string', type: 'String' },
        { name: 'expression', type: 'String' },
      ],
    },
    {
      name: pascal(FlowableEl.variable),
      superClass: ['Element'],
      properties: [attr('source'), attr('sourceExpression'), attr('target')],
    },
    {
      name: pascal(FlowableEl.variableAggregation),
      superClass: ['Element'],
      properties: [
        attr('target'),
        // 子<variable>はFlowableの手書きXMLどおりBPMN既定名前空間(接頭辞なし)で書かれている。
        // flowable:Variableとして素朴に定義するとパースで落ちるため、名前はbpmn:、型は完全修飾で分ける。
        {
          name: `bpmn:${FlowableEl.variable}`,
          type: flowableTypeName(FlowableEl.variable),
          isMany: true,
          xml: { serialize: 'property' },
        },
      ],
    },
    {
      name: 'ServiceTaskExtension',
      isAbstract: true,
      extends: ['bpmn:ServiceTask'],
      properties: [attr(FlowableAttr.type), attr(FlowableAttr.topic)],
    },
    {
      name: 'UserTaskExtension',
      isAbstract: true,
      extends: ['bpmn:UserTask'],
      properties: [attr(FlowableAttr.assignee)],
    },
    {
      name: 'MultiInstanceExtension',
      isAbstract: true,
      extends: ['bpmn:MultiInstanceLoopCharacteristics'],
      properties: [attr(FlowableAttr.collection), attr(FlowableAttr.elementVariable)],
    },
  ],
};
